import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "../styles/style.css";
import "../styles/header.css";
import BuildHubLogo from "../images/buildhub.png";
import BuildHubLogoSmall from "../images/buildhub-small.png";
import { userLogout } from "../hooks/accountHandler";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NotificationBell from "./reusable/notification-bell";
import { WebSocketContext } from "../context/contexts";
import { logoutSupport } from "../hooks/supportStore";

const MOBILE_QUERY = "(max-width: 767px)";

const APPS = [
  { key: "chat", label: "Chat", to: "/" },
  { key: "requests", label: "Requests", to: "/requests?p=view-requests" },
  { key: "support", label: "Support", to: "/support" },
];

const getCurrentApp = (pathname) => {
  if (pathname.startsWith("/requests")) return "requests";
  if (pathname.startsWith("/support")) return "support";
  if (pathname.startsWith("/profile")) return null;
  return "chat";
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = (e) => setIsMobile(e.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  return isMobile;
};

function Avatar({ userDetails }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = userDetails?.profile_photo;

  useEffect(() => setPhotoFailed(false), [photo]);

  if (photo && !photoFailed) {
    return (
      <img
        className="app-header__avatar"
        src={`${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${photo}`}
        alt=""
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  return (
    <span className="app-header__avatar app-header__avatar--initials" aria-hidden="true">
      {getInitials(userDetails?.display_name)}
    </span>
  );
}

function Header(props) {
  const WS = useContext(WebSocketContext);

  const { userDetails, showHideChangePasswordModal, inProfile } = props;
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const currentApp = getCurrentApp(location.pathname);
  // Support is only for cs_access users; the support page itself passes a
  // trimmed user object, so also keep the tab when already on that page.
  const apps = APPS.filter(
    (app) =>
      app.key !== "support" ||
      userDetails?.cs_access ||
      currentApp === "support"
  );
  const currentAppLabel = APPS.find((app) => app.key === currentApp)?.label;
  const canChangePassword =
    !inProfile && typeof showHideChangePasswordModal === "function";

  const closeMenu = useCallback((returnFocus = true) => {
    setMenuOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return;

    const getItems = () =>
      [...(menuRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    getItems()[0]?.focus();

    const handleKeyDown = (e) => {
      const items = getItems();
      const index = items.indexOf(document.activeElement);

      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        items[(index + 1) % items.length]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        items[items.length - 1]?.focus();
      } else if (e.key === "Tab") {
        closeMenu(false);
      }
    };

    const handlePointerDown = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      )
        return;
      closeMenu(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    let previousOverflow;
    if (isMobile) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      if (isMobile) document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen, isMobile, closeMenu]);

  const runMenuAction = (action) => {
    closeMenu(false);
    action();
  };

  const handleLogout = () => {
    userLogout();
    if (WS && WS.websocketInstance) {
      WS.websocketInstance.logout();
    }
    logoutSupport();
    navigate("/login");
  };

  const menu = (
    <div
      ref={menuRef}
      id="app-header-menu"
      className="app-header__menu"
      role="menu"
      aria-label="Account"
    >
      <div className="app-header__menu-who">
        <Avatar userDetails={userDetails} />
        <div className="app-header__menu-who-text">
          <p className="app-header__menu-name">{userDetails?.display_name}</p>
          {userDetails?.email_address && (
            <p className="app-header__menu-email">{userDetails.email_address}</p>
          )}
        </div>
      </div>

      {isMobile && (
        <div className="app-header__menu-section" role="group" aria-label="Apps">
          {apps.map((app) => (
            <button
              key={app.key}
              type="button"
              role="menuitem"
              className={`app-header__menu-item ${
                app.key === currentApp ? "app-header__menu-item--current" : ""
              }`}
              aria-current={app.key === currentApp ? "page" : undefined}
              onClick={() => runMenuAction(() => navigate(app.to))}
            >
              {app.label}
            </button>
          ))}
        </div>
      )}

      <div className="app-header__menu-section">
        <button
          type="button"
          role="menuitem"
          className="app-header__menu-item"
          onClick={() => runMenuAction(() => navigate("/profile"))}
        >
          My profile
        </button>
        <button
          type="button"
          role="menuitem"
          className="app-header__menu-item"
          onClick={() => runMenuAction(() => navigate("/requests?p=view-requests"))}
        >
          My requests
        </button>
        {canChangePassword && (
          <button
            type="button"
            role="menuitem"
            className="app-header__menu-item"
            onClick={() => runMenuAction(showHideChangePasswordModal)}
          >
            Change password
          </button>
        )}
      </div>

      <div className="app-header__menu-section">
        <button
          type="button"
          role="menuitem"
          className="app-header__menu-item app-header__menu-item--danger"
          onClick={() => runMenuAction(handleLogout)}
        >
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <header className="app-header">
      <Link to="/" className="app-header__brand" aria-label="BuildHub Chat home">
        <picture>
          <source media={MOBILE_QUERY} srcSet={BuildHubLogoSmall} />
          <img src={BuildHubLogo} alt="BuildHub" className="app-header__logo" />
        </picture>
      </Link>

      <nav className="app-header__apps" aria-label="Apps">
        {apps.map((app) => (
          <Link
            key={app.key}
            to={app.to}
            className={`app-header__app ${
              app.key === currentApp ? "app-header__app--current" : ""
            }`}
            aria-current={app.key === currentApp ? "page" : undefined}
          >
            {app.label}
          </Link>
        ))}
      </nav>

      {currentAppLabel && (
        <span className="app-header__context">{currentAppLabel}</span>
      )}

      <div className="app-header__actions">
        <NotificationBell
          addtlClass="app-header__bell"
          userId={userDetails?.id}
          displayName={userDetails?.display_name}
        />

        <button
          ref={triggerRef}
          type="button"
          className="app-header__account"
          onClick={() => setMenuOpen((prevState) => !prevState)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? "app-header-menu" : undefined}
          aria-label={`Account menu for ${userDetails?.display_name || "you"}`}
        >
          <Avatar userDetails={userDetails} />
          <span className="app-header__account-name">
            {userDetails?.display_name}
          </span>
          <i className="fa fa-angle-down app-header__caret" aria-hidden="true"></i>
        </button>

        {menuOpen &&
          (isMobile
            ? createPortal(
                <div className="app-header-sheet">
                  <div className="app-header-sheet__backdrop" aria-hidden="true" />
                  {menu}
                </div>,
                document.body
              )
            : menu)}
      </div>
    </header>
  );
}

export default Header;
